import Link from 'next/link';
import { IconAdd } from '@sympla/sympla-components/ActionIcons';
import { ReactNode, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import IconBuger from 'assets/icons/ic-burguer.svg';
import IconUser from 'assets/icons/ic-user.svg';
import IconCalendar from 'assets/icons/ic-calendar.svg';
import IconLogout from 'assets/icons/ic-logout.svg';
import IconTickets from 'assets/icons/ic-ticket.svg';
import {
  Button,
  Dropdown,
  IconFavorite,
  IconQuestion,
} from '@sympla/sympla-components';
import { getMainUrl, getMainUrlOrganizer, isMobile } from 'utils/misc';
import { PAGES_URL } from 'constants/index';
import { getInitials } from 'utils/getInitials';
import useAuth from 'hooks/useAuth';
import * as styles from './UserSpace.css';
import UserSection from '../UserSection/UserSection';

interface NewUserSpaceProps {
  handleLogout: () => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (value: boolean) => void;
  isNavbarCollapsed?: boolean;
  handleTrankingUserSpace: (label: string) => void;
}

function NewUserSpace({
  handleLogout,
  isDropdownOpen,
  setIsDropdownOpen,
  isNavbarCollapsed = false,
  handleTrankingUserSpace,
}: NewUserSpaceProps) {
  const authContainerRef = useRef(null);
  const authStore = useAuth();
  const fullName = authStore?.userData?.dataUser.fullName || '';
  const email = authStore?.userData?.dataUser.email || '';

  useEffect(() => {
    if (!authStore.isAuthenticated && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [authStore.isAuthenticated, authStore.userData, isDropdownOpen, setIsDropdownOpen]);

  function buttonAction() {
    if (authStore.isAuthenticated) {
      if (isMobile()) {
        return authStore.setModalAuthenticatedVisible(true);
      }
      return setIsDropdownOpen(true);
    }
    return authStore.openSignInModal();
  }

  const userIcon: ReactNode = authStore.isAuthenticated && fullName
    ? getInitials(fullName)
    : <IconUser width={20} height={20} />;

  return (
    <div className={styles.buttonsContainer}>
      <div className={`${styles.linksContainer} ${isNavbarCollapsed && styles.linksContainerCollapsed}`}>
        <Link
          className={styles.linkSm}
          target="_blank"
          rel="noopener noreferrer"
          id="btn-create-event"
          href={`${getMainUrlOrganizer()}${PAGES_URL.organizerCreateEvent}`}
        >
          <IconAdd className={styles.linkIcon} />
          Criar evento
        </Link>

        <Link
          className={styles.linkSm}
          target="_blank"
          rel="noopener noreferrer"
          id="btn-my-event"
          href={`${getMainUrlOrganizer()}${PAGES_URL.organizerArea}`}
        >
          <IconCalendar className={styles.linkIcon} />
          Meus eventos
        </Link>

        {authStore.isAuthenticated ? (
          <Link
            className={styles.link}
            rel="noopener noreferrer"
            href={`${getMainUrl()}${PAGES_URL.myTickets}`}
            id="btn-my-tickets"
            onClick={() => handleTrankingUserSpace('Meus ingressos')}
          >
            <IconTickets className={styles.linkIcon} />
            <span className={styles.linkLabelXs}>Ingressos</span>
            <span className={styles.linkLabelSm}>Meus ingressos</span>
          </Link>
        ) : (
          <Button
            className={styles.link}
            onClick={() => {
              authStore.openSignInModal();
              handleTrankingUserSpace('Meus ingressos');
            }}
            upperCase={false}
            icon={(
              <IconTickets className={styles.linkIcon} />
            )}
            id="btn-my-tickets"
          >
            <span className={styles.linkLabelXs}>Ingressos</span>
            <span className={styles.linkLabelSm}>Meus ingressos</span>
          </Button>
        )}
      </div>

      <div className={styles.dropdownContainer} ref={authContainerRef}>
        <Dropdown
          topDistance="0px"
          isOpen={isDropdownOpen && authStore.isAuthenticated}
          options={[]}
          onOpenChange={() => setIsDropdownOpen(false)}
          side="bottom"
          container={authContainerRef.current}
          trigger={(
            <button
              type="button"
              className={
                styles.userButtonDropdown[
                  (isDropdownOpen && authStore.isAuthenticated) ? 'focus' : 'default'
                ]
              }
              onClick={() => buttonAction()}
            >
              <IconBuger width={24} height={24} />
              <div className={styles.avatar}>{userIcon}</div>
            </button>
          )}
        >
          {authStore.isAuthenticated && (
            <div className={styles.customDropdown} style={{ height: `${isDropdownOpen ? '100%' : '0px'}` }}>
              <div className={styles.customDropdownSection.dropdownUserSection}>
                <UserSection avatar={userIcon} email={email} fullName={fullName} />
              </div>
              <div className={styles.customDropdownSection.dropdownLinkSection}>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${process.env.NEXT_PUBLIC_ORGANIZER_URL}${PAGES_URL.myAccount}`}
                  className={`${styles.dropdownLinkItem} svg-primary-stroke`}
                  onClick={() => handleTrankingUserSpace('Minha conta')}
                >
                  <IconUser width={18} height={18} />
                  Minha conta
                </Link>
                <Link href={PAGES_URL.favorite} className={styles.dropdownLinkItem} onClick={() => handleTrankingUserSpace('Favoritos')}>
                  <IconFavorite width={18} height={18} />
                  Favoritos
                </Link>
                <Link
                  className={`${styles.dropdownLinkItem} ${styles.mobileOnly}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${getMainUrlOrganizer()}${PAGES_URL.organizerCreateEvent}`}
                  onClick={() => handleTrankingUserSpace('Criar evento')}
                >
                  <IconAdd />
                  Criar evento
                </Link>

                <Link
                  className={`${styles.dropdownLinkItem} ${styles.mobileOnly}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${getMainUrlOrganizer()}${PAGES_URL.organizerArea}`}
                >
                  <IconCalendar />
                  Meus eventos
                </Link>
              </div>
              <div className={styles.customDropdownSection.dropdownLinkSection}>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={PAGES_URL.helpCenter}
                  className={styles.dropdownLinkItem}
                  onClick={() => handleTrankingUserSpace('Central de Ajuda')}
                >
                  <IconQuestion width={18} height={18} />
                  Central de Ajuda
                </Link>
              </div>
              <div className={styles.customDropdownSection.dropdownLinkSection}>
                <button type="button" className={`${styles.dropdownLinkItem} svg-primary-stroke`} onClick={() => handleLogout()}>
                  <IconLogout width={18} height={18} />
                  Sair
                </button>
              </div>
            </div>
          )}
        </Dropdown>
      </div>
    </div>
  );
}

export default observer(NewUserSpace);
